/**
 * Announcement Bar Timer
 *
 * A countdown timer custom element for the announcement bar.
 *
 * Features:
 * - Two modes: Evergreen (auto-restart) and One-time (expires permanently)
 * - Persists target time in localStorage so the countdown survives page navigation
 * - Dismissible per session via sessionStorage
 * - Optional sticky behavior (position: fixed) that exposes a CSS variable
 *   (--announcement-bar-timer-height) for body padding and a
 *   :root[data-timer-sticky] attribute. While sticky, the timer also
 *   suspends the site header's own sticky behavior (by stashing and removing
 *   its `sticky` attribute) so only the timer stays pinned to the viewport.
 * - Theme editor compatible (handles shopify:section:load/unload)
 */

class AnnouncementBarTimer extends HTMLElement {
  static STICKY_ROOT_ATTR = "timerSticky";
  static STICKY_HEIGHT_VAR = "--announcement-bar-timer-height";
  static HEADER_SELECTOR = "#header-component";
  static HEADER_SUSPENDED_ATTR = "data-sticky-suspended";
  static TIGHT_CLASS = "announcement-bar-timer--tight";
  static STACKED_CLASS = "announcement-bar-timer--stacked";

  /** @type {number | null} */
  #interval = null;
  /** @type {number | null} */
  #targetTime = null;
  #boundDismiss = this.#dismiss.bind(this);
  #boundVisibilityChange = this.#handleVisibilityChange.bind(this);
  #boundResize = this.#scheduleLayout.bind(this);
  /** @type {number | null} */
  #layoutFrame = null;

  connectedCallback() {
    this.timerMode = this.dataset.timerMode || "evergreen";
    this.expiredBehavior = this.dataset.expiredBehavior || "hide";
    this.expiredMessage = this.dataset.expiredMessage || "Sale has ended!";
    this.sticky = this.dataset.sticky === "true";

    const hoursAttr = this.dataset.countdownHours;
    const minutesAttr = this.dataset.countdownMinutes;
    const secondsAttr = this.dataset.countdownSeconds;

    this.countdownHours = this.#parseDataNumber(hoursAttr, 5, 0);
    this.countdownMinutes = this.#parseDataNumber(minutesAttr, 0, 0);
    this.countdownSeconds = this.#parseDataNumber(secondsAttr, 0, 0);

    this.daysElement = this.querySelector("[data-days]");
    this.hoursElement = this.querySelector("[data-hours]");
    this.minutesElement = this.querySelector("[data-minutes]");
    this.secondsElement = this.querySelector("[data-seconds]");
    this.messageElement = this.querySelector("[data-message]");
    this.countdownElement = this.querySelector(".announcement-bar-timer__countdown");
    this.closeButton = this.querySelector("[data-close-timer]");

    this.storageKey = `announcement-bar-timer-${this.id}`;
    this.dismissedKey = `${this.storageKey}-dismissed`;
    this.expiredKey = `${this.storageKey}-expired`;

    this.#init();
  }

  disconnectedCallback() {
    this.#destroy();
  }

  #parseDataNumber(value, fallback, min) {
    if (value === undefined || value === "") return fallback;
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed) || parsed < min) return fallback;
    return parsed;
  }

  #init() {
    if (this.#isDismissed()) {
      this.#hide();
      return;
    }

    // SLEEPSAKE EDIT (DEV-314, DEV-706): in calendar and midnight mode the
    // clock (and in calendar mode the phase and message) belong to
    // sale-timer.js; this element keeps the sticky behaviour and the fit.
    // Midnight mode must never reach the evergreen path below, which would
    // write the per-visitor localStorage clock sale-timer.js removes.
    if (this.timerMode === "calendar" || this.timerMode === "midnight") {
      if (this.closeButton) {
        this.closeButton.addEventListener("click", this.#boundDismiss);
      }
      window.addEventListener("resize", this.#boundResize);
      // sale-timer.js swaps the bar's content in place at a phase change,
      // which changes its height without a resize.
      document.addEventListener("sk:sale-phase", this.#boundResize);
      this.#fitMessage();
      this.#applyStickyState();
      this.#refitWhenFontsLoad();
      return;
    }

    if (this.timerMode === "one_time" && this.#isPermanentlyExpired()) {
      this.#showExpiredState();
      this.#applyStickyState();
      return;
    }

    this.#calculateTargetTime();

    if (this.#targetTime === null || Number.isNaN(this.#targetTime)) {
      console.warn("AnnouncementBarTimer: Failed to set target time");
      return;
    }

    this.#updateDisplay();
    this.#startTimer();

    if (this.closeButton) {
      this.closeButton.addEventListener("click", this.#boundDismiss);
    }

    document.addEventListener("visibilitychange", this.#boundVisibilityChange);
    window.addEventListener("resize", this.#boundResize);

    this.#fitMessage();
    this.#applyStickyState();
    this.#refitWhenFontsLoad();
  }

  /**
   * SLEEPSAKE (DEV-706): a message beside the tiles stays on one line. When it
   * overflows, shrink it; if it still overflows, stack it above the tiles.
   * Without visible tiles the message wraps freely and nothing applies.
   */
  #fitMessage() {
    const { TIGHT_CLASS, STACKED_CLASS } = AnnouncementBarTimer;
    this.classList.remove(TIGHT_CLASS, STACKED_CLASS);
    const message = this.messageElement;
    if (!message || !this.countdownElement || this.countdownElement.hidden) return;
    const fits = () => message.scrollWidth <= message.clientWidth;
    if (fits()) return;
    this.classList.add(TIGHT_CLASS);
    if (fits()) return;
    this.classList.remove(TIGHT_CLASS);
    this.classList.add(STACKED_CLASS);
  }

  /** Resize and phase changes come in bursts; lay out once per frame. */
  #scheduleLayout() {
    if (this.#layoutFrame !== null) return;
    this.#layoutFrame = requestAnimationFrame(() => {
      this.#layoutFrame = null;
      this.#fitMessage();
      this.#applyStickyState();
    });
  }

  #refitWhenFontsLoad() {
    if (document.fonts && document.fonts.status !== "loaded") {
      document.fonts.ready.then(() => {
        if (this.isConnected) this.#scheduleLayout();
      });
    }
  }

  #isDismissed() {
    try {
      return sessionStorage.getItem(this.dismissedKey) === "true";
    } catch (_) {
      return false;
    }
  }

  #isPermanentlyExpired() {
    try {
      return localStorage.getItem(this.expiredKey) === "true";
    } catch (_) {
      return false;
    }
  }

  #markPermanentlyExpired() {
    try {
      localStorage.setItem(this.expiredKey, "true");
    } catch (_) {
      // localStorage unavailable
    }
  }

  #calculateTargetTime() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const storedTime = parseInt(stored, 10);
        if (!Number.isNaN(storedTime) && storedTime > Date.now()) {
          this.#targetTime = storedTime;
          return;
        }
        localStorage.removeItem(this.storageKey);

        if (this.timerMode === "one_time" && !Number.isNaN(storedTime)) {
          this.#markPermanentlyExpired();
          this.#showExpiredState();
          return;
        }
      }
    } catch (_) {
      // localStorage unavailable
    }

    const totalMs = this.#totalCountdownMs();
    this.#targetTime = Date.now() + totalMs;

    try {
      localStorage.setItem(this.storageKey, this.#targetTime.toString());
    } catch (_) {
      // localStorage unavailable - timer will reset on page navigation
    }
  }

  #totalCountdownMs() {
    return this.countdownHours * 60 * 60 * 1000 + this.countdownMinutes * 60 * 1000 + this.countdownSeconds * 1000;
  }

  #getTimeRemaining() {
    const remaining = (this.#targetTime ?? 0) - Date.now();

    if (remaining <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    }

    const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
    const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds, expired: false };
  }

  #updateDisplay() {
    const time = this.#getTimeRemaining();

    let displayHours = time.hours;
    if (!this.daysElement && time.days > 0) {
      displayHours = time.days * 24 + time.hours;
    }

    this.#updateNumber(this.daysElement, time.days);
    this.#updateNumber(this.hoursElement, displayHours);
    this.#updateNumber(this.minutesElement, time.minutes);
    this.#updateNumber(this.secondsElement, time.seconds);

    if (time.expired) {
      this.#handleExpired();
    }
  }

  #updateNumber(element, value) {
    if (!element) return;

    const formatted = value.toString().padStart(2, "0");
    if (element.textContent === formatted) return;

    element.textContent = formatted;
    element.classList.add("is-updating");

    setTimeout(() => {
      element.classList.remove("is-updating");
    }, 150);
  }

  #handleExpired() {
    this.#stopTimer();

    try {
      localStorage.removeItem(this.storageKey);
    } catch (_) {
      // localStorage unavailable
    }

    if (this.timerMode === "one_time") {
      this.#markPermanentlyExpired();
      this.#showExpiredState();
    } else {
      this.#restartTimer();
    }
  }

  #showExpiredState() {
    if (this.expiredBehavior === "hide") {
      this.#hide();
    } else {
      this.classList.add("announcement-bar-timer--expired");
      if (this.messageElement && this.expiredMessage) {
        this.messageElement.textContent = this.expiredMessage;
      }
      this.#updateNumber(this.daysElement, 0);
      this.#updateNumber(this.hoursElement, 0);
      this.#updateNumber(this.minutesElement, 0);
      this.#updateNumber(this.secondsElement, 0);
    }
    this.#stopTimer();
  }

  #restartTimer() {
    this.#targetTime = Date.now() + this.#totalCountdownMs();

    try {
      localStorage.setItem(this.storageKey, this.#targetTime.toString());
    } catch (_) {
      // localStorage unavailable
    }

    this.#updateDisplay();
    this.#startTimer();
  }

  #startTimer() {
    this.#stopTimer();
    this.#interval = window.setInterval(() => this.#updateDisplay(), 1000);
  }

  #stopTimer() {
    if (this.#interval !== null) {
      clearInterval(this.#interval);
      this.#interval = null;
    }
  }

  #handleVisibilityChange() {
    if (!document.hidden) {
      this.#updateDisplay();
    }
  }

  #dismiss() {
    try {
      sessionStorage.setItem(this.dismissedKey, "true");
    } catch (_) {
      // sessionStorage unavailable
    }
    this.#hide();
    this.#stopTimer();
  }

  #hide() {
    this.classList.add("announcement-bar-timer--hidden");
    this.#applyStickyState();
  }

  /**
   * Manage the sticky CSS variable + :root[data-timer-sticky] attribute that
   * controls body padding, and suspend the site header's sticky behavior
   * so only the timer stays pinned at the top of the viewport.
   */
  #applyStickyState() {
    if (!this.sticky) {
      this.#clearStickyState();
      return;
    }

    // SLEEPSAKE EDIT (DEV-706): midnight mode hides the bar with `hidden`
    // when the visitor's own date reaches a hide_bar block.
    if (this.classList.contains("announcement-bar-timer--hidden") || this.hidden) {
      this.#clearStickyState();
      return;
    }

    document.documentElement.dataset[AnnouncementBarTimer.STICKY_ROOT_ATTR] = "true";
    this.#updateStickyHeight();
    this.#suspendHeaderSticky();
  }

  #clearStickyState() {
    delete document.documentElement.dataset[AnnouncementBarTimer.STICKY_ROOT_ATTR];
    document.documentElement.style.setProperty(AnnouncementBarTimer.STICKY_HEIGHT_VAR, "0px");
    this.#restoreHeaderSticky();
  }

  /**
   * Re-derive the document-level sticky state from whatever timers remain in the
   * DOM. The sticky root attribute and height variable are global, but the theme
   * editor unloads sections one at a time, and the unloaded one may never have
   * held a timer. Clearing unconditionally would drop a surviving bar's body
   * padding while it stayed position:fixed, overlaying the header until a full
   * reload.
   *
   * Teardown of a section that did hold a timer is already handled by that
   * element's disconnectedCallback.
   */
  static resyncStickyState() {
    for (const timer of document.querySelectorAll("announcement-bar-timer")) {
      if (timer instanceof AnnouncementBarTimer && timer.isConnected) {
        timer.#applyStickyState();
        return;
      }
    }

    delete document.documentElement.dataset[AnnouncementBarTimer.STICKY_ROOT_ATTR];
    document.documentElement.style.setProperty(AnnouncementBarTimer.STICKY_HEIGHT_VAR, "0px");
  }

  /**
   * Stash the header's `sticky` attribute on a non-reactive data attribute so
   * the header.liquid CSS rule for [sticky='always' | 'scroll-up'] no longer
   * matches and the header scrolls away normally.
   */
  #suspendHeaderSticky() {
    const header = document.querySelector(AnnouncementBarTimer.HEADER_SELECTOR);
    if (!header || !header.hasAttribute("sticky")) return;

    header.setAttribute(AnnouncementBarTimer.HEADER_SUSPENDED_ATTR, header.getAttribute("sticky") || "");
    header.removeAttribute("sticky");
  }

  #restoreHeaderSticky() {
    const header = document.querySelector(`[${AnnouncementBarTimer.HEADER_SUSPENDED_ATTR}]`);
    if (!header) return;

    const original = header.getAttribute(AnnouncementBarTimer.HEADER_SUSPENDED_ATTR);
    if (original) header.setAttribute("sticky", original);
    header.removeAttribute(AnnouncementBarTimer.HEADER_SUSPENDED_ATTR);
  }

  #updateStickyHeight() {
    if (!this.sticky) return;
    if (this.classList.contains("announcement-bar-timer--hidden") || this.hidden) return;

    const height = this.offsetHeight;
    document.documentElement.style.setProperty(AnnouncementBarTimer.STICKY_HEIGHT_VAR, `${height}px`);
  }

  #destroy() {
    this.#stopTimer();
    if (this.#layoutFrame !== null) cancelAnimationFrame(this.#layoutFrame);

    if (this.closeButton) {
      this.closeButton.removeEventListener("click", this.#boundDismiss);
    }

    document.removeEventListener("visibilitychange", this.#boundVisibilityChange);
    window.removeEventListener("resize", this.#boundResize);
    document.removeEventListener("sk:sale-phase", this.#boundResize);

    this.#clearStickyState();
  }
}

if (!customElements.get("announcement-bar-timer")) {
  customElements.define("announcement-bar-timer", AnnouncementBarTimer);
}

// Theme editor support: clear stored countdown state on section reload so the
// merchant always sees a fresh preview when they tweak settings.
if (typeof Shopify !== "undefined" && Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    target.querySelectorAll("announcement-bar-timer").forEach((element) => {
      try {
        const key = `announcement-bar-timer-${element.id}`;
        localStorage.removeItem(key);
        localStorage.removeItem(`${key}-expired`);
        sessionStorage.removeItem(`${key}-dismissed`);
      } catch (_) {
        // storage unavailable
      }
    });
  });

  document.addEventListener("shopify:section:unload", () => {
    AnnouncementBarTimer.resyncStickyState();
  });
}
